import { createSlice, PayloadAction } from '@reduxjs/toolkit'

export type TechniqueConfig = {
  techniqueId: string
  name: string
  workDuration: number // seconds
  breakDuration: number // seconds
  longBreakDuration?: number // seconds
  cycles?: number
  color?: string // Tailwind color class used in UI (optional)
  icon?: string // Emoji or icon string
}

export interface TimeTechniqueState {
  isActive: boolean
  isBreak: boolean
  currentCycle: number
  timeLeft: number // seconds
  started: boolean
  showOverlay: boolean
  technique: TechniqueConfig | null
  lastUpdatedAt: number | null // ms timestamp
}

const initialState: TimeTechniqueState = {
  isActive: false,
  isBreak: false,
  currentCycle: 1,
  timeLeft: 0,
  started: false,
  showOverlay: false,
  technique: null,
  lastUpdatedAt: null,
}

const timeTechniqueSlice = createSlice({
  name: 'timeTechnique',
  initialState,
  reducers: {
    startTechnique: (state, action: PayloadAction<TechniqueConfig>) => {
      state.technique = action.payload
      state.isActive = true
      state.isBreak = false
      state.currentCycle = 1
      state.timeLeft = action.payload.workDuration
      state.started = true
      state.showOverlay = true
      state.lastUpdatedAt = Date.now()
    },
    pauseTechnique: (state) => {
      state.isActive = false
      state.lastUpdatedAt = Date.now()
    },
    resumeTechnique: (state) => {
      if (state.started && state.technique) {
        state.isActive = true
        state.lastUpdatedAt = Date.now()
      }
    },
    stopTechnique: (state) => {
      // Keep technique to allow quick restart, but stop and hide overlay
      state.isActive = false
      state.isBreak = false
      state.started = false
      state.showOverlay = false
      state.currentCycle = 1
      state.timeLeft = state.technique?.workDuration ?? 0
      state.lastUpdatedAt = null
      state.technique = null
    },
    setShowOverlay: (state, action: PayloadAction<boolean>) => {
      state.showOverlay = action.payload
    },
    tickTechnique: (state) => {
      if (!state.isActive || !state.technique || state.lastUpdatedAt === null) return

      const now = Date.now()
      const diffSec = Math.floor((now - state.lastUpdatedAt) / 1000)
      if (diffSec <= 0) return

      state.timeLeft = Math.max(0, state.timeLeft - diffSec)
      state.lastUpdatedAt = state.lastUpdatedAt + diffSec * 1000

      if (state.timeLeft === 0) {
        // Transition between phases
        if (!state.isBreak) {
          // Work finished -> Either stop (if cycles reached) or Break
          const cycles = state.technique?.cycles
          if (typeof cycles === 'number' && cycles > 0 && state.currentCycle >= cycles) {
            // Auto-stop after completing the configured number of work cycles
            state.isActive = false
            state.isBreak = false
            state.started = false
            state.showOverlay = false
            state.currentCycle = 1
            state.timeLeft = state.technique.workDuration
            state.lastUpdatedAt = null
            state.technique = null
            return
          }

          // Continue with break time
          state.isBreak = true
          const useLongBreak = state.technique.techniqueId === 'pomodoro' && !!state.technique.longBreakDuration && (state.currentCycle % 4 === 0)
          state.timeLeft = useLongBreak ? (state.technique.longBreakDuration || state.technique.breakDuration) : state.technique.breakDuration
          state.lastUpdatedAt = now
        } else {
          // Break finished -> Next Work
          state.isBreak = false
          state.currentCycle += 1
          state.timeLeft = state.technique.workDuration
          state.lastUpdatedAt = now
        }
      }
    },
    setTechniqueDirect: (state, action: PayloadAction<Partial<TechniqueConfig>>) => {
      if (!state.technique) return
      state.technique = { ...state.technique, ...action.payload }
    },
    setTimeLeftDirect: (state, action: PayloadAction<number>) => {
      state.timeLeft = action.payload
      state.lastUpdatedAt = Date.now()
    }
  }
})

export const {
  startTechnique,
  pauseTechnique,
  resumeTechnique,
  stopTechnique,
  setShowOverlay,
  tickTechnique,
  setTechniqueDirect,
  setTimeLeftDirect,
} = timeTechniqueSlice.actions

export default timeTechniqueSlice.reducer